import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { ColumnDef } from '@tanstack/react-table'
import { Eye, Trash2 } from 'lucide-react'
import { useMemo, useState } from 'react'
import { EnhancedTable } from '@/shared/components/table/EnhancedTable'
import { Button } from '@/shared/components/ui/button'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card'
import { toast } from '@/shared/lib/toast'
import {
  type ContractAddon,
  type ContractAddonReference,
  contractService,
} from '../../services/contractService'
import { AddonDetailsDialog } from './AddonDetailsDialog'
import { AddonDialog } from './AddonDialog'

interface AddonsSectionProps {
  mode: 'create' | 'edit'
  // For create mode - now uses simplified addon references
  addons?: ContractAddon[] | ContractAddonReference[]
  onAddAddon?: () => void
  onRemoveAddon?: (addonId: string) => void
  // For edit mode
  contractId?: string
  // For view-only mode (hide actions)
  viewOnly?: boolean
}

export function AddonsSection({
  mode,
  addons: localAddons = [],
  onAddAddon,
  onRemoveAddon,
  contractId,
  viewOnly = false,
}: AddonsSectionProps) {
  const [selectedAddon, setSelectedAddon] = useState<ContractAddon | null>(null)
  const [isAddonDetailsDialogOpen, setIsAddonDetailsDialogOpen] =
    useState(false)
  const queryClient = useQueryClient()

  // Fetch contract addons (only in edit mode)
  const { data: fetchedAddons = [], isLoading } = useQuery({
    queryKey: ['contract-addons', contractId],
    queryFn: () => contractService.getAddons(contractId!),
    enabled: mode === 'edit' && !!contractId,
  })

  // Fetch addon plan details for create mode
  const { data: availableAddonPlans = [] } = useQuery({
    queryKey: ['addon-plans'],
    queryFn: contractService.getAvailableAddons,
    enabled: mode === 'create',
  })

  // Fetch line items for calculating addon charges (only in edit mode)
  const { data: allLineItems = [] } = useQuery({
    queryKey: ['contract-line-items', contractId],
    queryFn: () => contractService.getAddonLineItems(contractId!),
    enabled: mode === 'edit' && !!contractId,
  })

  // Convert addon references to display format for create mode
  const displayAddons =
    mode === 'create'
      ? (localAddons as ContractAddonReference[]).map(addonRef => {
          const addonPlan = availableAddonPlans.find(
            plan => plan.id === addonRef.plan_id
          )
          const monthlyCharges = addonPlan
            ? contractService.calculateAddonPlanMonthlyTotal(addonPlan)
            : 0

          return {
            id: addonRef.plan_id,
            plan_id: addonRef.plan_id,
            name: addonPlan?.name || 'Unknown Addon',
            description: addonPlan?.description || '',
            type: addonPlan?.billing_interval || 'unknown',
            amount: monthlyCharges,
            currency: addonPlan?.currency || 'USD',
            quantity: 1,
            status: 'pending' as const,
            monthly_charges: monthlyCharges,
            proration: false,
            metadata: {},
          }
        })
      : fetchedAddons

  // Use appropriate addons based on mode
  const addons = displayAddons

  // Calculate charges for each addon (memoized for performance)
  const _addonCharges = useMemo(() => {
    if (mode === 'create' || !allLineItems.length) return {}

    const chargeMap: Record<string, number> = {}

    for (const addon of fetchedAddons) {
      const addonLineItems = allLineItems.filter(
        item => item.addon_id === addon.id
      )
      chargeMap[addon.id] =
        contractService.calculateAddonCharges(addonLineItems)
    }

    return chargeMap
  }, [mode, allLineItems, fetchedAddons])

  // Remove addon mutation (only for edit mode)
  const removeAddonMutation = useMutation({
    mutationFn: (addonId: string) =>
      contractService.deleteAddon(contractId!, addonId),
    onSuccess: () => {
      toast.success('Success', 'Addon removed from contract successfully')
      queryClient.invalidateQueries({
        queryKey: ['contract-addons', contractId],
      })
      queryClient.invalidateQueries({ queryKey: ['contract', contractId] })
    },
    onError: (error: Error) => {
      toast.error('Error', error.message || 'Failed to remove addon')
    },
  })

  // Handle view addon details
  const handleViewAddonDetails = (addon: ContractAddon) => {
    setSelectedAddon(addon)
    setIsAddonDetailsDialogOpen(true)
  }

  // Calculate total monthly cost for an addon - using the same logic as ContractPreviewCard
  const calculateAddonMonthlyCost = (addon: ContractAddon): number => {
    if (mode === 'create') {
      // For create mode, calculate from the addon plan data
      const addonPlan = availableAddonPlans.find(
        plan => plan.id === addon.plan_id
      )
      if (!addonPlan) return addon.monthly_charges || addon.amount || 0

      let totalRecurring = 0
      let totalUsage = 0

      // Process charges (recurring only for monthly cost)
      if (addonPlan.charges) {
        for (const charge of addonPlan.charges) {
          if ((charge as Record<string, unknown>).type === 'recurring') {
            totalRecurring +=
              parseFloat(String((charge as Record<string, unknown>).amount)) ||
              0
          }
        }
      }

      // Process features with minimum commitments
      if (addonPlan.features) {
        for (const feature of addonPlan.features) {
          const minCommitment = Number(feature.minimum_commitment_units) || 0
          if (minCommitment > 0) {
            let cost = 0

            if (feature.pricing_model === 'tiered' && feature.tier_pricing) {
              let remainingUnits = minCommitment
              for (const tier of feature.tier_pricing as Record<
                string,
                unknown
              >[]) {
                if (remainingUnits <= 0) break
                const tierMax =
                  tier.lastUnit === null || tier.lastUnit === undefined
                    ? Number.POSITIVE_INFINITY
                    : Number(tier.lastUnit)
                const tierMin = Number(tier.firstUnit) || 1
                const tierRange =
                  tierMax === Number.POSITIVE_INFINITY
                    ? remainingUnits
                    : tierMax - tierMin + 1
                const unitsInThisTier = Math.min(remainingUnits, tierRange)
                const tierPrice = Number(tier.perUnitPrice) || 0
                cost += unitsInThisTier * tierPrice
                remainingUnits -= unitsInThisTier
              }
            } else if (
              feature.pricing_model === 'volume' &&
              feature.tier_pricing
            ) {
              const applicableTier = (
                feature.tier_pricing as Record<string, unknown>[]
              ).find(
                (tier: Record<string, unknown>) =>
                  minCommitment >= (Number(tier.firstUnit) || 1) &&
                  (tier.lastUnit === null ||
                    minCommitment <= Number(tier.lastUnit))
              )
              if (applicableTier) {
                cost =
                  minCommitment * (Number(applicableTier.perUnitPrice) || 0)
              }
            } else {
              cost = minCommitment * (Number(feature.unit_price) || 0)
            }

            totalUsage += cost
          }
        }
      }

      // Process agents with minimum commitments
      if (addonPlan.agents) {
        for (const agent of addonPlan.agents) {
          if (agent.signals) {
            for (const signal of agent.signals as Record<string, unknown>[]) {
              const minCommitment = Number(signal.minimum_commitment_units) || 0
              if (minCommitment > 0) {
                let cost = 0

                if (
                  signal.pricing_model === 'tiered' &&
                  signal.tiers &&
                  (signal.tiers as unknown[]).length > 0
                ) {
                  let remainingUnits = minCommitment
                  for (const tier of signal.tiers as Record<
                    string,
                    unknown
                  >[]) {
                    if (remainingUnits <= 0) break
                    const tierMax =
                      tier.maxUnits === null || tier.maxUnits === undefined
                        ? Number.POSITIVE_INFINITY
                        : Number(tier.maxUnits)
                    const tierMin = Number(tier.minUnits) || 1
                    const tierRange =
                      tierMax === Number.POSITIVE_INFINITY
                        ? remainingUnits
                        : tierMax - tierMin + 1
                    const unitsInThisTier = Math.min(remainingUnits, tierRange)
                    const tierPrice = parseFloat(String(tier.unitPrice)) || 0
                    cost += unitsInThisTier * tierPrice
                    remainingUnits -= unitsInThisTier
                  }
                } else if (
                  signal.pricing_model === 'volume' &&
                  signal.tiers &&
                  (signal.tiers as unknown[]).length > 0
                ) {
                  const applicableTier = (
                    signal.tiers as Record<string, unknown>[]
                  ).find(
                    (tier: Record<string, unknown>) =>
                      minCommitment >= (Number(tier.minUnits) || 1) &&
                      (tier.maxUnits === null ||
                        minCommitment <= Number(tier.maxUnits))
                  )
                  if (applicableTier) {
                    cost =
                      minCommitment *
                      (parseFloat(String(applicableTier.unitPrice)) || 0)
                  }
                } else {
                  cost = minCommitment * (Number(signal.unit_price) || 0)
                }

                totalUsage += cost
              }
            }
          }
        }
      }

      return totalRecurring + totalUsage
    }
    // For edit mode, calculate from the fetched addon data using the exact same logic as ContractPreviewCard
    let totalRecurring = 0
    let totalUsage = 0

    // Process charges (recurring only for monthly cost)
    if (addon.charges) {
      for (const charge of addon.charges) {
        if (charge.charge_type === 'recurring') {
          totalRecurring += parseFloat(String(charge.amount)) || 0
        }
      }
    }

    // Process features with minimum commitments
    if (addon.features) {
      for (const feature of addon.features) {
        // Check tier pricing first
        const tierPricing = feature.tier_pricing as
          | Record<string, unknown>[]
          | undefined
        if (tierPricing && tierPricing.length > 0) {
          // Use tier pricing to determine cost based on actual minimum commitment units
          let cost = 0
          const minCommitment = Number(feature.minimum_commitment_units) || 0

          for (const tier of tierPricing) {
            const tierCost =
              (Number(tier.flatFee) || 0) +
              (Number(tier.perUnitPrice) || 0) *
                Math.min(
                  minCommitment,
                  (Number(tier.lastUnit) ||
                    Number(tier.maxUnits) ||
                    Number.POSITIVE_INFINITY) -
                    (Number(tier.firstUnit) || Number(tier.minUnits) || 1) +
                    1
                )
            cost += tierCost
          }

          totalUsage += cost
        } else {
          // Flat rate with minimum commitment
          const minCommitment = Number(feature.minimum_commitment_units) || 0
          if (minCommitment > 0) {
            const rate = Number(feature.overage_rate) || 0
            totalUsage += minCommitment * rate
          }
        }
      }
    }

    // Process agents with minimum commitments
    if (addon.agents) {
      for (const agent of addon.agents) {
        if (agent.signal_configurations) {
          for (const signal of agent.signal_configurations as Record<
            string,
            unknown
          >[]) {
            const minCommitment = Number(signal.minimum_commitment_units) || 0
            if (minCommitment > 0) {
              let cost = 0

              if (
                signal.pricing_model === 'tiered' &&
                signal.tiers &&
                (signal.tiers as unknown[]).length > 0
              ) {
                let remainingUnits = minCommitment
                for (const tier of signal.tiers as Record<string, unknown>[]) {
                  if (remainingUnits <= 0) break
                  const tierMax =
                    tier.maxUnits === null || tier.maxUnits === undefined
                      ? Number.POSITIVE_INFINITY
                      : Number(tier.maxUnits)
                  const tierMin = Number(tier.minUnits) || 1
                  const tierRange =
                    tierMax === Number.POSITIVE_INFINITY
                      ? remainingUnits
                      : tierMax - tierMin + 1
                  const unitsInThisTier = Math.min(remainingUnits, tierRange)
                  const tierPrice = parseFloat(String(tier.unitPrice)) || 0
                  cost += unitsInThisTier * tierPrice
                  remainingUnits -= unitsInThisTier
                }
              } else if (
                signal.pricing_model === 'volume' &&
                signal.tiers &&
                (signal.tiers as unknown[]).length > 0
              ) {
                const applicableTier = (
                  signal.tiers as Record<string, unknown>[]
                ).find(
                  (tier: Record<string, unknown>) =>
                    minCommitment >= (Number(tier.minUnits) || 1) &&
                    (tier.maxUnits === null ||
                      minCommitment <= Number(tier.maxUnits))
                )
                if (applicableTier) {
                  cost =
                    minCommitment *
                    (parseFloat(String(applicableTier.unitPrice)) || 0)
                }
              } else {
                cost = minCommitment * (Number(signal.unit_price) || 0)
              }

              totalUsage += cost
            }
          }
        }
      }
    }

    return totalRecurring + totalUsage
  }

  const addonColumns: ColumnDef<ContractAddon>[] = [
    {
      accessorKey: 'name',
      header: 'Name',
      size: 300,
      cell: ({ row }) => (
        <div>
          <div className='font-medium'>{row.original.name}</div>
          <div className='text-sm text-muted-foreground'>
            {row.original.description}
          </div>
        </div>
      ),
    },
    {
      accessorKey: 'amount',
      header: 'Total Monthly Cost',
      size: 200,
      cell: ({ row }) => {
        const totalMonthlyCost = calculateAddonMonthlyCost(row.original)
        return (
          <div className='font-medium text-md'>
            ${totalMonthlyCost.toFixed(2)}/month + usage
          </div>
        )
      },
    },
    ...(viewOnly
      ? []
      : [
          {
            id: 'actions',
            header: 'Actions',
            size: 100,
            cell: ({ row }: { row: { original: ContractAddon } }) => (
              <div className='flex items-center gap-1'>
                {mode === 'edit' && (
                  <Button
                    variant='ghost'
                    size='sm'
                    onClick={() => handleViewAddonDetails(row.original)}
                    title='View Details'
                  >
                    <Eye className='w-4 h-4' />
                  </Button>
                )}
                {mode === 'create' && onRemoveAddon && (
                  <Button
                    variant='ghost'
                    size='sm'
                    onClick={() => onRemoveAddon(row.original.id)}
                    className='text-destructive hover:text-destructive hover:bg-destructive/10'
                    title='Remove Addon'
                  >
                    <Trash2 className='w-4 h-4' />
                  </Button>
                )}
                {mode === 'edit' && (
                  <Button
                    variant='ghost'
                    size='sm'
                    onClick={() => removeAddonMutation.mutate(row.original.id)}
                    className='text-destructive hover:text-destructive hover:bg-destructive/10'
                    title='Remove Addon'
                    disabled={removeAddonMutation.isPending}
                  >
                    <Trash2 className='w-4 h-4' />
                  </Button>
                )}
              </div>
            ),
          },
        ]),
  ]

  return (
    <>
      <Card className='bg-transparent border'>
        <CardHeader>
          <div className='flex items-center justify-between'>
            <CardTitle>Contract Add-ons</CardTitle>
            {mode === 'create' && onAddAddon && (
              <Button onClick={onAddAddon} variant='outline' size='sm'>
                Add Addon
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className='text-center py-8 text-muted-foreground'>
              Loading addons...
            </div>
          ) : addons.length === 0 ? (
            <div className='text-center py-8 text-muted-foreground'>
              <div className='border border-dashed border-muted-foreground/30 rounded-lg p-6'>
                <div
                  className={`grid gap-4 text-sm font-medium ${viewOnly ? 'grid-cols-2' : 'grid-cols-3'}`}
                >
                  <div>Name</div>
                  <div>Total Monthly Cost</div>
                  {!viewOnly && <div>Actions</div>}
                </div>
                <div
                  className={`grid gap-4 text-center py-4 text-muted-foreground ${viewOnly ? 'grid-cols-2' : 'grid-cols-3'}`}
                >
                  <div>--</div>
                  <div>--</div>
                  {!viewOnly && <div>--</div>}
                </div>
                <p className='text-sm mt-4'>
                  No addons added to this contract yet.
                </p>
              </div>
            </div>
          ) : (
            <div className='[&_table]:bg-transparent [&_thead]:bg-transparent [&_tr:hover]:bg-slate-50/50 dark:[&_tr:hover]:bg-slate-800/50'>
              <EnhancedTable
                data={addons}
                columns={addonColumns.map(col => ({
                  ...col,
                  enableSorting: false,
                }))}
                height='auto'
                className='bg-transparent'
              />
            </div>
          )}
        </CardContent>
      </Card>

      {mode === 'edit' && contractId && (
        <AddonDialog
          open={false}
          onOpenChange={() => {}}
          contractId={contractId}
          existingAddonIds={
            addons.map(addon => addon.plan_id).filter(Boolean) as string[]
          }
        />
      )}

      {mode === 'edit' && contractId && selectedAddon && (
        <AddonDetailsDialog
          open={isAddonDetailsDialogOpen}
          onOpenChange={setIsAddonDetailsDialogOpen}
          addon={selectedAddon}
          contractId={contractId}
        />
      )}
    </>
  )
}
